import type { RoomPresenceDto } from '../../types/community'
import { Card } from '../ui/Card'
import { Avatar } from '../ui/Avatar'
import { CopyButton } from '../ui/CopyButton'
import { EmptyState } from '../ui/EmptyState'

interface PresenceCardProps {
  presence: RoomPresenceDto[]
  /** Avatar tint index by personId, when known (from the room's people). */
  tintFor?: (personId: string) => number
}

function stateText(state: string): string {
  switch (state) {
    case 'Host':
      return 'Host'
    case 'InGame':
      return 'In game'
    default:
      return 'In the lobby'
  }
}

function glyph(firstName: string): string {
  const trimmed = (firstName || '').trim()
  return trimmed ? trimmed[0].toUpperCase() : '•'
}

/** "In this room" presence list with per-person Copy in-game name (COTC_Rooms R2 / DESIGN §3.5). */
export function PresenceCard({ presence, tintFor }: PresenceCardProps) {
  return (
    <Card padding="md">
      <div className="text-[14px] font-semibold text-ink">In this room</div>

      {presence.length === 0 ? (
        <EmptyState className="mt-2" description="Nobody is here yet. When friends join, they show up here." />
      ) : (
        <ul className="mt-3 space-y-3">
          {presence.map((p) => {
            const copyName = p.handle && p.handle.length > 0 ? p.handle : p.firstName
            return (
              <li key={p.personId} className="flex items-center gap-3">
                <Avatar initials={glyph(p.firstName)} tintIndex={tintFor?.(p.personId) ?? 0} size={32} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13.5px] font-medium text-ink">{p.firstName}</div>
                  <div className="truncate font-mono text-[11px] text-ink-faint">
                    {p.handle ? `${p.handle} · ` : ''}
                    {stateText(p.state)}
                  </div>
                </div>
                <CopyButton value={copyName} what="In-game name">
                  Copy name
                </CopyButton>
              </li>
            )
          })}
        </ul>
      )}

      <p className="mt-4 border-t border-line-row pt-3 text-[12px] leading-relaxed text-ink-faint">
        Copy a name to add them inside the game — friend lists on the game itself are up to each family.
      </p>
    </Card>
  )
}
